import React, { useEffect, useRef, useState, useMemo } from 'react';
import './DonutChart.css';
import { Box, Typography } from '@mui/material';
import Chart from 'chart.js/auto';
import SubjectInstructor from '../UserTeacherAnalytics/SubjectInstructor/SubjectInstructor';

const DonutChart = ({ title, centerValue, data, colors, icons, isUserAnalytics = false }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubjectType, setSelectedSubjectType] = useState(null);

  // Compute final colors with session type mapping taking precedence
  const sessionTypeColorMap = {
    'one-on-one': '#6B60E7',
    group: '#279BE2',
    prime: '#AD8B00',
  };
  const defaultColors = ['#6B60E7', '#279BE2', '#AD8B00', '#ef4444'];

  const finalColors = useMemo(() => {
    const mappedColors = (data || []).map(item => sessionTypeColorMap[item.label?.toLowerCase()]);
    return data && mappedColors.length === data.length && mappedColors.every(Boolean)
      ? mappedColors
      : colors || defaultColors;
  }, [data, colors]);

  useEffect(() => {
    // Create the chart
    if (chartRef.current) {
      // Destroy existing chart instance if it exists
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');

      // Prepare data for Chart.js
      const chartData = {
        labels: data.map(item => item.label),
        datasets: [
          {
            data: data.map(item => item.value),
            backgroundColor: finalColors.slice(0, data.length),
            borderWidth: 0,
            cutout: '60%',
            borderRadius: 15,
            offset: 5,
          },
        ],
      };

      // Create the chart
      chartInstance.current = new Chart(ctx, {
        type: 'doughnut',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: context => {
                  const label = context.label || '';
                  const value = context.raw || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  // const percentage = Math.round((value / total) * 100);
                  return `${label}: ${value}`;
                },
              },
            },
          },
          onClick: (event, elements) => {
            // Only handle clicks if this is the user analytics donut chart
            if (isUserAnalytics && elements.length > 0) {
              const clickedIndex = elements[0].index;
              const clickedLabel = data[clickedIndex]?.label?.toLowerCase();

              // Map labels to subject types
              if (clickedLabel === 'academic') {
                setSelectedSubjectType('academic');
                setModalOpen(true);
              } else if (clickedLabel === 'creative') {
                setSelectedSubjectType('creative');
                setModalOpen(true);
              }
            }
          },
        },
        plugins: [
          {
            id: 'centerText',
            beforeDraw: chart => {
              const { ctx, chartArea } = chart;
              ctx.save();

              // Draw 'Total' text
              ctx.font = '14px Poppins';
              ctx.fillStyle = '#6b7280';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(
                'Total',
                chartArea.left + chartArea.width / 2,
                chartArea.top + chartArea.height / 2 - 10
              );

              // Draw centerValue
              ctx.font = '700 24px Poppins';
              ctx.fillStyle = '#111827';
              ctx.fillText(
                centerValue,
                chartArea.left + chartArea.width / 2,
                chartArea.top + chartArea.height / 2 + 15
              );

              ctx.restore();
            },
          },
        ],
      });
    }

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, centerValue, colors, isUserAnalytics, finalColors]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSubjectType(null);
  };

  const handleLegendClick = label => {
    if (!isUserAnalytics) return;

    const labelLower = label.toLowerCase();
    if (labelLower === 'academic') {
      setSelectedSubjectType('academic');
      setModalOpen(true);
    } else if (labelLower === 'creative') {
      setSelectedSubjectType('creative');
      setModalOpen(true);
    }
  };

  // Get the total instructors for the selected subject type
  const getSelectedSubjectInstructors = () => {
    if (!selectedSubjectType) return 0;
    const subjectData = data.find(
      item =>
        item.label.toLowerCase() ===
        (selectedSubjectType === 'academic' ? 'academic' : 'creative art')
    );
    return subjectData?.value || 0;
  };

  return (
    <Box className={`donut-chart-container ${isUserAnalytics ? 'user-analytics-clickable' : ''}`}>
      <Typography className="donut-title">{title}</Typography>
      <Box className="donut-chart">
        <canvas ref={chartRef} />
      </Box>
      <Box className="donut-legend">
        {data.map((item, index) => (
          <Box
            key={index}
            className="donut-legend-item"
            onClick={() => handleLegendClick(item.label)}
          >
            {icons && icons[index] ? (
              <img src={icons[index]} alt={item.label} className="donut-legend-icon" />
            ) : (
              <span
                className="donut-legend-dot"
                style={{ backgroundColor: finalColors[index] ?? `var(--color-${index + 1})` }}
              ></span>
            )}
            <Typography className="donut-legend-text">{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Subject Instructor Modal */}
      {isUserAnalytics && (
        <SubjectInstructor
          open={modalOpen}
          onClose={handleCloseModal}
          subjectType={selectedSubjectType}
          totalInstructors={getSelectedSubjectInstructors()}
        />
      )}
    </Box>
  );
};

export default DonutChart;
